import {
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CubeIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import type { CBCTPatient } from '../components/cbct/CBCTViewer';
import type { ScanStorage } from '../components/cbct/scanStore';
import type { FullStlViewerFile } from '../components/stl/FullStlViewer';
import { ConfirmModal } from './ConfirmModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { ErrorBanner } from './ErrorBanner';
import { FileCard } from './FileCard';
import { FileCategorySection } from './FileCategorySection';
import { FileRow } from './FileRow';
import { FileToolbar } from './FileToolbar';
import { FilesConfigProvider, type FilesConfigValue } from './config';
import { PreviewContent } from './PreviewContent';
import { RenameFileModal } from './RenameFileModal';
import { SelectionBar } from './SelectionBar';
import { UploadZoneContent } from './UploadZoneContent';
import { useFilesManager } from './useFilesManager';
import { useImagePreviewActions } from './useImagePreviewActions';
import type {
    Asset,
    CbctExportHandler,
    FileCategoryConfig,
    FilesApi,
    FilesCapabilities,
    RenameModalExtrasContext
} from './types';
import { formatCreatedDate } from './utils';
import { resolveFilesCapabilities } from './types';

// Lazy so the three.js stack stays out of the host's main chunk (same pattern
// as PreviewContent's viewers).
const FullStlViewer = React.lazy(() => import('../components/stl/FullStlViewer'));

const EmptyState: React.FC<{ searchTerm: string }> = ({ searchTerm }) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            {searchTerm ? (
                <MagnifyingGlassIcon className="w-6 h-6 text-gray-400" />
            ) : (
                <CubeIcon className="w-6 h-6 text-gray-400" />
            )}
        </div>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">
            {searchTerm ? 'No files found' : 'No files available'}
        </h3>
        <p className="text-gray-400 text-xs">
            {searchTerm
                ? `No files match "${searchTerm}".`
                : 'This patient has no files to display.'}
        </p>
    </div>
);

export interface FilesPanelProps {
    patientId: string;
    /** Shown (with the scan's upload date) in the CBCT viewer's top bar. */
    patientName?: string;
    /** Merged asset map, keyed by assetId - supplied by the host (react-query etc.). */
    assets: Record<string, Asset>;
    isLoading: boolean;
    error?: unknown;
    refresh: () => Promise<void>;
    api: FilesApi;
    capabilities?: FilesCapabilities;
    initialPreviewFileIds?: string[] | null;
    onPreviewChange?: (fileIds: string[] | null) => void;
    /** Host slot rendered inside the edit modal (e.g. CBCT processing trigger). */
    renameModalExtras?: (ctx: RenameModalExtrasContext) => React.ReactNode;
    /** Backend-backed persistence for CBCT markings. Required to open CBCT scans. */
    cbctScanStorage?: ScanStorage;
    /** App-side upload of exported Pano/Ceph PNGs; the panel defers the asset
     *  refresh until the user returns to the list. */
    onCbctExport?: CbctExportHandler;
    /** Base URL of the host's API - used by the PDF/STL viewers' proxy fetches. */
    viewerApiBaseUrl?: string;
    /** Bearer token supplier for the PDF viewer's proxy fetches. */
    getAuthToken?: () => string | null;
    /** 'card' wraps the panel in white-card chrome; 'none' (default) renders bare. */
    chrome?: 'card' | 'none';
    /** localStorage key prefix for layout/list mode persistence. */
    storageKeyPrefix?: string;
    /** Overrides the "By category" grouping. Defaults to the built-in
     *  extension/storageCategory taxonomy - see `FileCategoryConfig`. */
    categoryConfig?: FileCategoryConfig;
    /** Rendered in place of the built-in upload drop zone when the toolbar's
     *  Upload toggle is open - for hosts whose uploads need extra fields the
     *  zone can't host (e.g. the practice Files section's document type +
     *  description form). Absent = the built-in UploadZoneContent. */
    uploadSection?: React.ReactNode;
}

const FilesPanel: React.FC<FilesPanelProps> = ({
    patientId,
    patientName,
    assets,
    isLoading,
    error,
    refresh,
    api,
    capabilities,
    initialPreviewFileIds,
    onPreviewChange,
    renameModalExtras,
    cbctScanStorage,
    onCbctExport,
    viewerApiBaseUrl = '',
    getAuthToken,
    chrome = 'none',
    storageKeyPrefix,
    categoryConfig,
    uploadSection
}) => {
    const caps = useMemo(() => resolveFilesCapabilities(capabilities), [capabilities]);

    const manager = useFilesManager({
        patientId,
        patientName,
        assets,
        refreshAssets: refresh,
        api,
        capabilities: caps,
        initialPreviewFileIds,
        onPreviewChange,
        storageKeyPrefix,
        categoryConfig
    });

    const {
        searchTerm,
        setSearchTerm,
        listMode,
        layoutMode,
        updateLayoutMode,
        updateListMode,
        expandedCategories,
        toggleCategory,
        fileCategories,
        dateViewAssets,
        dateGroups,
        filesCountText,
        viewMode,
        previewableAssets,
        previewIndex,
        currentPreviewAsset,
        currentPreviewType,
        currentPreviewUrl,
        isLoadingPreview,
        previewError,
        setPreviewError,
        cbctSources,
        handlePreviewFile,
        handlePreviewCategory,
        navigatePreview,
        goBackToList,
        notifyCbctExported,
        selection,
        download,
        onDownloadSelected,
        onDownloadCategory,
        pendingDownload,
        confirmPendingDownload,
        cancelPendingDownload,
        deleteRename,
        handleDeleteSelected,
        upload,
        thumbnails,
        cbctProcessing,
        hasGcsSelected
    } = manager;

    const previewActions = useImagePreviewActions({
        asset: viewMode === 'preview' ? currentPreviewAsset : null,
        isImagePreview: currentPreviewType === 'image',
        patientId,
        api,
        capabilities: caps,
        downloadFile: download.downloadSingleFile,
        requestDelete: deleteRename.handleDeleteFile,
        requestEdit: deleteRename.handleRenameFile,
        isDeleting: deleteRename.isDeleting,
        refreshAssets: refresh
    });

    const filesConfig = useMemo<FilesConfigValue>(
        () => ({ ...caps, cbctProcessingEnabled: cbctProcessing.enabled }),
        [caps, cbctProcessing.enabled]
    );

    // Defer the export-triggered refresh until the user leaves the preview so
    // the save doesn't shift previewIndex and swap the viewer out mid-session.
    const handleCbctExport = useMemo(() => {
        if (!onCbctExport) return undefined;
        const wrapped: CbctExportHandler = async (png, info) => {
            const result = await onCbctExport(png, info);
            notifyCbctExported();
            return result;
        };
        return wrapped;
    }, [onCbctExport, notifyCbctExported]);

    const onRetry = useCallback(() => {
        void refresh();
    }, [refresh]);

    // --- Full STL viewer (opened from the 3D Models section header) ---
    const [stlViewerOpen, setStlViewerOpen] = useState(false);
    const stlViewerFiles = useMemo<FullStlViewerFile[]>(() => {
        const cat = fileCategories.find((c) => c.label === '3D Models');
        // Only GCS-backed files can be resolved through FilesApi.getDownloadUrl.
        return (cat?.assets ?? [])
            .filter((a) => a.source === 'gcs' && a.patientFileId)
            .map((a) => ({
                id: a.patientFileId as string,
                name: a.name,
                createdAt: a.createdAt,
                uploadedBy: a.uploadedBy?.name
            }));
    }, [fileCategories]);
    const getStlFileUrl = useCallback(
        (fileId: string) => api.getDownloadUrl(patientId, fileId),
        [api, patientId]
    );

    const chromeClass = chrome === 'card' ? 'bg-white rounded-xl border border-gray-200' : '';

    // First-load skeleton: show only while we have no data yet. Subsequent
    // refetches keep the existing list visible to avoid a flash of empty state.
    const showInitialLoading = isLoading && Object.keys(assets).length === 0;
    if (showInitialLoading) {
        return (
            <div className={`flex-1 min-h-0 flex items-center justify-center ${chromeClass}`}>
                <div className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }

    if (error !== undefined && error !== null && Object.keys(assets).length === 0) {
        return (
            <div
                className={`flex-1 min-h-0 flex flex-col items-center justify-center gap-3 p-8 ${chromeClass}`}
            >
                <div className="text-xs text-red-500">Failed to load files</div>
                <button
                    onClick={onRetry}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <FilesConfigProvider value={filesConfig}>
            <div className={`flex-1 min-h-0 flex flex-col ${chromeClass}`}>
                {viewMode === 'list' && !stlViewerOpen && (
                    <FileToolbar
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        selectionMode={selection.selectionMode}
                        onToggleSelectionMode={selection.toggleSelectionMode}
                        showUploadZone={upload.showUploadZone}
                        onToggleUploadZone={upload.toggleUploadZone}
                        layoutMode={layoutMode}
                        onLayoutModeChange={updateLayoutMode}
                        listMode={listMode}
                        onListModeChange={updateListMode}
                        filesCountText={filesCountText}
                    />
                )}

                {/* Content */}
                <div
                    className={`flex-1 min-h-0 ${viewMode === 'preview' || stlViewerOpen ? 'overflow-hidden flex flex-col' : 'overflow-y-auto scrollbar-hide'}`}
                >
                    {stlViewerOpen ? (
                        /* Full STL viewer - presented like the CBCT preview: in the
                           panel with a "Back to files" header; fullscreen via its
                           own top-bar toggle. */
                        <div className="flex flex-col flex-1 min-h-0">
                            <div className="px-5 pt-4 pb-3 flex-shrink-0">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-semibold text-gray-800">
                                            STL Viewer
                                        </h3>
                                    </div>
                                    <div className="flex-1 flex justify-end min-w-0">
                                        <button
                                            onClick={() => setStlViewerOpen(false)}
                                            className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-xs font-medium"
                                        >
                                            <ArrowLeftIcon className="w-3 h-3" />
                                            Back to files
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="relative flex-1 min-h-0 overflow-hidden">
                                {/* Edge to edge, like the CBCT viewer's wrapper. */}
                                <div className="absolute inset-0 z-10">
                                    <Suspense
                                        fallback={
                                            <div className="flex items-center justify-center w-full h-full bg-gray-50">
                                                <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                            </div>
                                        }
                                    >
                                        <FullStlViewer
                                            files={stlViewerFiles}
                                            getFileUrl={getStlFileUrl}
                                            onClose={() => setStlViewerOpen(false)}
                                        />
                                    </Suspense>
                                </div>
                            </div>
                        </div>
                    ) : viewMode === 'list' ? (
                        <div className="px-5 py-4 space-y-3">
                            {upload.showUploadZone && uploadSection}
                            {upload.showUploadZone && !uploadSection && (
                                <UploadZoneContent
                                    isUploading={upload.isUploading}
                                    uploadSuccess={upload.uploadSuccess}
                                    uploadedCount={upload.uploadedCount}
                                    totalUploadCount={upload.totalUploadCount}
                                    uploadProgress={upload.uploadProgress}
                                    currentUploadingFile={upload.currentUploadingFile}
                                    stagedFiles={upload.stagedFiles}
                                    isDragOver={upload.isDragOver}
                                    uploadError={upload.uploadError}
                                    onDrop={upload.handleDrop}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        upload.setIsDragOver(true);
                                    }}
                                    onDragLeave={(e) => {
                                        e.preventDefault();
                                        upload.setIsDragOver(false);
                                    }}
                                    onFileInputChange={upload.handleFileInputChange}
                                    onRemoveFile={(i) =>
                                        upload.setStagedFiles((prev) =>
                                            prev.filter((_, j) => j !== i)
                                        )
                                    }
                                    onClearAll={() => upload.setStagedFiles([])}
                                    onUpload={upload.uploadFiles}
                                    onClearError={upload.clearUploadError}
                                />
                            )}

                            {download.downloadError && (
                                <ErrorBanner
                                    tone="amber"
                                    message={download.downloadError}
                                    onDismiss={download.clearDownloadError}
                                />
                            )}

                            {deleteRename.deleteError && (
                                <ErrorBanner
                                    tone="red"
                                    message={deleteRename.deleteError}
                                    onDismiss={deleteRename.clearDeleteError}
                                />
                            )}

                            {previewError && (
                                <ErrorBanner
                                    tone="amber"
                                    message={previewError}
                                    onDismiss={() => setPreviewError(null)}
                                />
                            )}

                            {selection.selectionMode && (
                                <SelectionBar
                                    selectedCount={selection.selectedAssets.size}
                                    onSelectAll={selection.selectAllVisible}
                                    onClearSelection={selection.clearSelection}
                                    onDownloadSelected={() => void onDownloadSelected()}
                                    onDeleteSelected={
                                        caps.canDelete ? handleDeleteSelected : undefined
                                    }
                                    isDownloading={selection.isDownloadingSelected}
                                    isDeleting={deleteRename.isDeleting}
                                    hasGcsSelected={hasGcsSelected}
                                />
                            )}

                            {listMode === 'category' ? (
                                fileCategories.length === 0 ? (
                                    <EmptyState searchTerm={searchTerm} />
                                ) : (
                                    fileCategories.map((category) => (
                                        <FileCategorySection
                                            key={category.label}
                                            category={category}
                                            isExpanded={expandedCategories.has(category.label)}
                                            isDownloading={
                                                download.downloadingCategory === category.label
                                            }
                                            layoutMode={layoutMode}
                                            thumbnailUrls={thumbnails.thumbnailUrls}
                                            onToggle={() => toggleCategory(category.label)}
                                            onPreviewFile={handlePreviewFile}
                                            onPreviewCategory={() =>
                                                handlePreviewCategory(category)
                                            }
                                            onOpenStlViewer={
                                                category.label === '3D Models' &&
                                                stlViewerFiles.length > 0
                                                    ? () => setStlViewerOpen(true)
                                                    : undefined
                                            }
                                            onDownloadCategory={() =>
                                                void onDownloadCategory(category)
                                            }
                                            onDownloadFile={download.downloadSingleFile}
                                            onDeleteFile={
                                                caps.canDelete
                                                    ? deleteRename.handleDeleteFile
                                                    : undefined
                                            }
                                            onRenameFile={
                                                caps.canRename
                                                    ? deleteRename.handleRenameFile
                                                    : undefined
                                            }
                                            selectedAssets={selection.selectedAssets}
                                            onToggleSelect={selection.toggleAssetSelection}
                                            selectionMode={selection.selectionMode}
                                            onThumbnailError={thumbnails.handleThumbnailError}
                                            thumbUrlAssetIds={thumbnails.thumbUrlAssetIds}
                                            processingAssetIds={cbctProcessing.activeAssetIds}
                                        />
                                    ))
                                )
                            ) : dateViewAssets.length === 0 ? (
                                <EmptyState searchTerm={searchTerm} />
                            ) : layoutMode === 'grid' ? (
                                <div className="space-y-4">
                                    {dateGroups.map((group) => (
                                        <div key={group.label}>
                                            <p className="text-xs font-semibold text-gray-500 mb-2">
                                                {group.label}
                                            </p>
                                            <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3">
                                                {group.assets.map((asset) => (
                                                    <FileCard
                                                        key={asset.assetId}
                                                        asset={asset}
                                                        thumbnailUrl={
                                                            thumbnails.thumbnailUrls[
                                                                String(asset.assetId)
                                                            ]
                                                        }
                                                        onPreviewFile={handlePreviewFile}
                                                        isSelected={selection.selectedAssets.has(
                                                            String(asset.assetId)
                                                        )}
                                                        onToggleSelect={
                                                            selection.toggleAssetSelection
                                                        }
                                                        selectionMode={selection.selectionMode}
                                                        onThumbnailError={
                                                            thumbnails.thumbUrlAssetIds.has(
                                                                String(asset.assetId)
                                                            )
                                                                ? thumbnails.handleThumbnailError
                                                                : undefined
                                                        }
                                                        isProcessing={cbctProcessing.activeAssetIds.has(
                                                            String(asset.assetId)
                                                        )}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {dateViewAssets.map((asset) => (
                                        <FileRow
                                            key={asset.assetId}
                                            asset={asset}
                                            onPreviewFile={handlePreviewFile}
                                            onDownloadFile={download.downloadSingleFile}
                                            onDeleteFile={
                                                caps.canDelete
                                                    ? deleteRename.handleDeleteFile
                                                    : undefined
                                            }
                                            onRenameFile={
                                                caps.canRename
                                                    ? deleteRename.handleRenameFile
                                                    : undefined
                                            }
                                            isSelected={selection.selectedAssets.has(
                                                String(asset.assetId)
                                            )}
                                            onToggleSelect={selection.toggleAssetSelection}
                                            selectionMode={selection.selectionMode}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col flex-1 min-h-0">
                            <div className="px-5 pt-4 pb-3 flex-shrink-0">
                                <div className="flex items-center justify-between gap-3">
                                    {/* Left: filename + upload date */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-semibold text-gray-800 truncate">
                                            {currentPreviewAsset?.name ?? ''}
                                        </h3>
                                        {currentPreviewAsset?.createdAt && (
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Uploaded{' '}
                                                {formatCreatedDate(currentPreviewAsset.createdAt)}
                                            </p>
                                        )}
                                    </div>

                                    {/* Center: Prev - counter - Next (hidden when navigation
                                        is disabled) */}
                                    {caps.enablePreviewNavigation && (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <button
                                                onClick={() => navigatePreview(-1)}
                                                disabled={
                                                    previewIndex === null || previewIndex <= 0
                                                }
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                                                aria-label="Previous file"
                                            >
                                                <ChevronLeftIcon className="w-3 h-3" />
                                                Prev
                                            </button>
                                            {previewIndex !== null &&
                                                previewableAssets.length > 0 && (
                                                    <span className="text-xs text-gray-500 tabular-nums min-w-[3.5rem] text-center">
                                                        {previewIndex + 1} of{' '}
                                                        {previewableAssets.length}
                                                    </span>
                                                )}
                                            <button
                                                onClick={() => navigatePreview(1)}
                                                disabled={
                                                    previewIndex === null ||
                                                    previewIndex >= previewableAssets.length - 1
                                                }
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                                                aria-label="Next file"
                                            >
                                                Next
                                                <ChevronRightIcon className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Right: Back to files */}
                                    <div className="flex-1 flex justify-end min-w-0">
                                        <button
                                            onClick={goBackToList}
                                            className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-xs font-medium"
                                        >
                                            <ArrowLeftIcon className="w-3 h-3" />
                                            Back to files
                                        </button>
                                    </div>
                                </div>
                                {caps.showPreviewActions && deleteRename.deleteError && (
                                    <div className="mt-3">
                                        <ErrorBanner
                                            tone="red"
                                            message={deleteRename.deleteError}
                                            onDismiss={deleteRename.clearDeleteError}
                                        />
                                    </div>
                                )}
                                {previewActions.error && (
                                    <div className="mt-3">
                                        <ErrorBanner
                                            tone="amber"
                                            message={previewActions.error}
                                            onDismiss={previewActions.clearError}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="relative flex-1 min-h-0 overflow-hidden px-5 pb-4">
                                {isLoadingPreview && !currentPreviewUrl ? (
                                    <div className="flex items-center justify-center w-full h-full bg-gray-50 rounded-lg">
                                        <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                    </div>
                                ) : currentPreviewType === 'cbct' ? (
                                    (() => {
                                        const cbctAsset = currentPreviewAsset;
                                        if (!cbctAsset) return null;
                                        const key = String(cbctAsset.assetId);
                                        const job = cbctProcessing.jobs[key];
                                        // PreviewContent owns the gate-vs-viewer rule; the
                                        // panel just supplies the gate props when the
                                        // capability exists, plus signed URLs once resolved.
                                        const gateProps = cbctProcessing.enabled
                                            ? {
                                                  phase: job?.phase ?? ('checking' as const),
                                                  error: job?.error,
                                                  onProcess: (password?: string) =>
                                                      void cbctProcessing.start(cbctAsset, password)
                                              }
                                            : undefined;
                                        const src = cbctSources[key];
                                        const cbctAssets: Asset[] = src
                                            ? [
                                                  { ...cbctAsset, publicUrl: src.manifestUrl },
                                                  ...(src.volumeUrl
                                                      ? [
                                                            {
                                                                ...cbctAsset,
                                                                publicUrl: src.volumeUrl
                                                            }
                                                        ]
                                                      : [])
                                              ]
                                            : [cbctAsset];
                                        const cbctPatient: CBCTPatient = {
                                            name: patientName,
                                            capturedAt: cbctAsset.createdAt
                                        };
                                        return (
                                            <PreviewContent
                                                previewType="cbct"
                                                previewAssets={cbctAssets}
                                                apiBaseUrl={viewerApiBaseUrl}
                                                getAuthToken={getAuthToken}
                                                cbctStorage={cbctScanStorage}
                                                onCbctExport={handleCbctExport}
                                                cbctPatient={cbctPatient}
                                                cbctReadOnly={caps.cbctReadOnly}
                                                cbctProcessingGate={gateProps}
                                            />
                                        );
                                    })()
                                ) : currentPreviewAsset &&
                                  currentPreviewType &&
                                  currentPreviewUrl ? (
                                    <PreviewContent
                                        previewType={currentPreviewType}
                                        previewAssets={[
                                            {
                                                ...currentPreviewAsset,
                                                publicUrl: currentPreviewUrl
                                            }
                                        ]}
                                        apiBaseUrl={viewerApiBaseUrl}
                                        getAuthToken={getAuthToken}
                                        imageActions={previewActions.actions}
                                    />
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>

                {/* Large-ZIP download warning */}
                {pendingDownload && (
                    <ConfirmModal
                        title="Download as ZIP?"
                        message={pendingDownload.message}
                        confirmLabel="Download"
                        onConfirm={() => void confirmPendingDownload()}
                        onCancel={cancelPendingDownload}
                    />
                )}

                {/* Delete confirmation modal */}
                {deleteRename.deleteTargets.length > 0 && (
                    <DeleteConfirmModal
                        assets={deleteRename.deleteTargets}
                        isDeleting={deleteRename.isDeleting}
                        onConfirm={() => void deleteRename.confirmDelete()}
                        onCancel={deleteRename.cancelDelete}
                    />
                )}

                {/* Rename modal */}
                {deleteRename.renameTarget && (
                    <RenameFileModal
                        asset={deleteRename.renameTarget}
                        isRenaming={deleteRename.isRenaming}
                        error={deleteRename.renameError}
                        canReclassify={caps.canReclassify}
                        extras={renameModalExtras}
                        onConfirm={(changes) => void deleteRename.confirmRename(changes)}
                        onCancel={deleteRename.cancelRename}
                    />
                )}
            </div>
        </FilesConfigProvider>
    );
};

export { FilesPanel };
